import React from 'react';

import {
  Collapse,
  FormControlLabel,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { TourOfferConfiguration, TourOfferConfigurationDetailsInput } from '~/types/tours/tourOffer';

import NativeTourCard from './NativeTourCard';
import NativeTourImageField from './NativeTourImageField';
import NativeTourInclusionList from './NativeTourInclusionList';
import NativeTourPlaceField from './NativeTourPlaceField';
import { optionDetailsProblems } from './nativeTourForm';

export type NativeTourOptionDetailsArea = 'details' | 'route' | 'packages';

interface Props {
  area: NativeTourOptionDetailsArea;
  configuration: TourOfferConfiguration;
  value: TourOfferConfigurationDetailsInput;
  onChange: (value: TourOfferConfigurationDetailsInput) => void;
  leastPax: number;
}

const integerOrNull = (value: string) => (value.trim() === '' ? null : Math.trunc(Number(value)));

/** An option's details, route or packages, one area at a time. */
export default function NativeTourOptionDetails({ area, configuration, value, onChange, leastPax }: Props) {
  const problems = optionDetailsProblems(value, leastPax);
  const set = (change: Partial<TourOfferConfigurationDetailsInput>) => onChange({ ...value, ...change });
  const setRoute = (change: Partial<TourOfferConfigurationDetailsInput['route']>) =>
    set({ route: { ...value.route, ...change } });

  if (area === 'details') {
    const numberField = (
      field: 'minTravellerAge' | 'maxTravellerAge' | 'minPax' | 'maxPax',
      label: string,
      error: string | undefined,
    ) => (
      <TextField
        type="number"
        label={label}
        value={value[field] ?? ''}
        onChange={(event) => set({ [field]: integerOrNull(event.target.value) })}
        error={!!error}
        helperText={error}
        inputProps={{ min: 0, step: 1 }}
        fullWidth
      />
    );
    return (
      <Stack spacing={3}>
        <NativeTourCard title="Who it suits" description="Traveller ages and the group size this option runs with.">
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, md: 3 }}>{numberField('minTravellerAge', 'Minimum age', problems.ages)}</Grid>
            <Grid size={{ xs: 6, md: 3 }}>{numberField('maxTravellerAge', 'Maximum age', undefined)}</Grid>
            <Grid size={{ xs: 6, md: 3 }}>{numberField('minPax', 'Minimum pax', problems.pax)}</Grid>
            <Grid size={{ xs: 6, md: 3 }}>{numberField('maxPax', 'Maximum pax', undefined)}</Grid>
          </Grid>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Switch
                checked={value.isReverseItinerary}
                onChange={(event) => set({ isReverseItinerary: event.target.checked })}
              />
            }
            label="Show as the alternate route"
          />
        </NativeTourCard>
        <NativeTourCard
          title="Private requests"
          description="Let customers ask for a private departure of this option."
        >
          <FormControlLabel
            control={
              <Switch
                checked={value.privateRequest.enabled}
                onChange={(event) =>
                  set({ privateRequest: { ...value.privateRequest, enabled: event.target.checked } })
                }
              />
            }
            label="Private requests available"
          />
          <Collapse in={value.privateRequest.enabled}>
            <TextField
              type="number"
              label="Starting from"
              value={value.privateRequest.fromPrice ?? ''}
              onChange={(event) =>
                set({
                  privateRequest: {
                    ...value.privateRequest,
                    fromPrice: event.target.value.trim() === '' ? null : Number(event.target.value),
                  },
                })
              }
              InputProps={{ startAdornment: <InputAdornment position="start">AUD $</InputAdornment> }}
              error={!!problems.privateRequest}
              helperText={problems.privateRequest ?? 'Converted to the other currencies when saved'}
              sx={{ mt: 2, maxWidth: 320 }}
              fullWidth
            />
          </Collapse>
        </NativeTourCard>
        {(configuration.content as { slug?: string | null } | null)?.slug && (
          <Typography variant="body2" color="text.secondary">
            Customer Portal link: …/{(configuration.content as { slug?: string | null }).slug}
          </Typography>
        )}
      </Stack>
    );
  }

  if (area === 'route') {
    const countries = configuration.itinerarySummary?.countriesVisited ?? [];
    return (
      <NativeTourCard title="Route" description="Where the option starts and ends, and the map customers see.">
        <Stack spacing={3}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <NativeTourPlaceField
                label="Start"
                value={{ name: value.route.startLocationName, country: value.route.startCountryName }}
                onChange={({ name, country }) => setRoute({ startLocationName: name, startCountryName: country })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <NativeTourPlaceField
                label="End"
                value={{ name: value.route.endLocationName, country: value.route.endCountryName }}
                onChange={({ name, country }) => setRoute({ endLocationName: name, endCountryName: country })}
              />
            </Grid>
          </Grid>
          <Typography variant="body2" color="text.secondary">
            Countries visited, from the itinerary's locations:{' '}
            {countries.length > 0 ? countries.join(', ') : 'none yet'}
          </Typography>
          <NativeTourImageField
            label="Route map"
            value={value.route.routeMapImage}
            onChange={(routeMapImage) => setRoute({ routeMapImage })}
          />
        </Stack>
      </NativeTourCard>
    );
  }

  const packageName = (id: string) => configuration.packages.find((pkg) => pkg.id === id)?.name ?? id;
  return (
    <NativeTourCard
      title="Packages"
      description="What each package adds on top of the option's own inclusions. Packages come from the supplier contracts."
    >
      <Stack spacing={2}>
        {value.packages.length === 0 && <Typography color="text.secondary">This option has no packages.</Typography>}
        {value.packages.map((pkg) => (
          <Paper key={pkg.id} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" mb={1.5}>
              {packageName(pkg.id)}
            </Typography>
            <NativeTourInclusionList
              value={pkg.inclusions}
              emptyText="Nothing added on top of the option."
              onChange={(inclusions) =>
                set({
                  packages: value.packages.map((other) => (other.id === pkg.id ? { ...other, inclusions } : other)),
                })
              }
            />
          </Paper>
        ))}
      </Stack>
    </NativeTourCard>
  );
}
